import React from 'react'
import { Badge, Button, Card, Col, Empty, Flex, Row, Skeleton, Tag, Typography, theme } from 'antd'
import dayjs from 'dayjs'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useSites } from '../hooks'
import type { Site } from '../site'

export function SitesOverview({ onAdd }: { onAdd?: () => void }) {
    const { t } = useTranslation()
    const { data: sites, isLoading } = useSites()

    if (isLoading) {
        return <Skeleton active />
    }

    if (!sites || sites.length === 0) {
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                    <Flex
                        vertical
                        gap={4}
                    >
                        <Typography.Text strong>{t('No websites yet')}</Typography.Text>
                        <Typography.Text type="secondary">
                            {t('Add one line of code to your website and Evaa is live within minutes.')}
                        </Typography.Text>
                    </Flex>
                }
                className="py-12"
            >
                {onAdd && (
                    <Button
                        type="primary"
                        onClick={onAdd}
                    >
                        {t('Add website')}
                    </Button>
                )}
            </Empty>
        )
    }

    const todo = sites.filter(hasTodo).sort((a, b) => Number(isError(b)) - Number(isError(a)))
    const live = sites.filter((site) => !todo.includes(site))

    return (
        <Flex
            vertical
            gap="large"
        >
            {todo.length > 0 && (
                <Flex
                    vertical
                    gap="small"
                >
                    <Typography.Title level={5}>
                        {t('To do')} ({todo.length})
                    </Typography.Title>
                    <div className="divide-y divide-neutral-200 rounded-xl border border-neutral-200 bg-white">
                        {todo.map((site) => (
                            <SiteTodo
                                key={site.id}
                                site={site}
                            />
                        ))}
                    </div>
                </Flex>
            )}
            {live.length > 0 && (
                <Flex
                    vertical
                    gap="small"
                >
                    {todo.length > 0 && (
                        <Typography.Title level={5}>
                            {t('Live')} ({live.length})
                        </Typography.Title>
                    )}
                    <Row gutter={[16, 16]}>
                        {live.map((site) => (
                            <Col
                                key={site.id}
                                xs={24}
                                md={12}
                                xl={8}
                            >
                                <LiveSite site={site} />
                            </Col>
                        ))}
                    </Row>
                </Flex>
            )}
        </Flex>
    )
}

/** Issues that keep the chat away from visitors: these websites are listed as to-dos instead of as live websites. */
const TODO: NonNullable<Site['issue']>[] = ['disconnected', 'no_plan', 'not_seen', 'not_installed', 'chat_disabled']

export function hasTodo(site: Site) {
    return !!site.issue && TODO.includes(site.issue)
}

export function isError(site: Site) {
    return site.issue === 'disconnected' || site.issue === 'not_seen'
}

/** A website that is not live yet or not anymore, in one row: what is wrong and a button to fix it. */
function SiteTodo({ site }: { site: Site }) {
    const issue = useIssue(site)

    return (
        <Flex
            align="center"
            gap="middle"
            wrap
            className="px-4 py-3"
        >
            <Link
                to="/websites/$siteId"
                params={{ siteId: site.id }}
                className="flex w-48 min-w-0 flex-col"
            >
                <Typography.Text
                    strong
                    ellipsis
                >
                    {site.name}
                </Typography.Text>
                <Typography.Text
                    type="secondary"
                    ellipsis
                >
                    {site.host}
                </Typography.Text>
            </Link>
            <Badge
                status={isError(site) ? 'error' : 'warning'}
                text={issue?.text}
                className="min-w-48 flex-1"
            />
            {issue?.action}
        </Flex>
    )
}

/** A website where visitors can chat: what the chat did this week. */
function LiveSite({ site }: { site: Site }) {
    const { t } = useTranslation()
    const chats = site.chatsLast7Days ?? 0

    return (
        <Card
            hoverable
            className="h-full overflow-hidden"
            styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column', height: '100%' } }}
        >
            <Link
                to="/websites/$siteId"
                params={{ siteId: site.id }}
                className="flex flex-1 flex-col gap-6 p-6"
            >
                <Flex
                    justify="space-between"
                    align="start"
                    gap="small"
                >
                    <Flex
                        vertical
                        className="min-w-0"
                    >
                        <Typography.Text
                            strong
                            ellipsis
                        >
                            {site.name}
                        </Typography.Text>
                        <Typography.Text
                            type="secondary"
                            ellipsis
                        >
                            {site.host}
                        </Typography.Text>
                    </Flex>
                    {site.type === 'wordpress' && <Tag className="me-0">{t('WordPress')}</Tag>}
                </Flex>
                {chats > 0 ? (
                    <Flex
                        justify="space-between"
                        align="end"
                        gap="middle"
                    >
                        <Flex vertical>
                            <Typography.Text>
                                <span className="text-2xl font-medium">{chats}</span>{' '}
                                {t('chats this week', { count: chats })}
                            </Typography.Text>
                            {(site.openVacanciesCount ?? 0) > 0 && (
                                <Typography.Text type="secondary">
                                    {t('Vacancy recommended in {{count}}', {
                                        count: site.recommendedChatsLast7Days ?? 0,
                                    })}
                                </Typography.Text>
                            )}
                        </Flex>
                        {site.chatsPerDay && <ChatsPerDay values={site.chatsPerDay} />}
                    </Flex>
                ) : (
                    <Typography.Text type="secondary">{t('No chats this week yet')}</Typography.Text>
                )}
            </Link>
            {site.issue && <SiteIssue site={site} />}
        </Card>
    )
}

/** Small bars of the chats per day, today last. */
function ChatsPerDay({ values }: { values: number[] }) {
    const { token } = theme.useToken()
    const max = Math.max(...values, 1)

    return (
        <svg
            width={values.length * 8}
            height={28}
            className="shrink-0"
        >
            {values.map((value, index) => {
                const height = Math.max(2, (value / max) * 28)

                return (
                    <rect
                        key={index}
                        x={index * 8}
                        y={28 - height}
                        width={6}
                        height={height}
                        rx={1}
                        fill={value > 0 ? token.colorPrimary : token.colorFillSecondary}
                    >
                        <title>
                            {dayjs()
                                .subtract(values.length - 1 - index, 'day')
                                .format('dd D MMM')}
                            : {value}
                        </title>
                    </rect>
                )
            })}
        </svg>
    )
}

/** What to fix first on a live site card, in a strip with a button to where it is fixed. */
function SiteIssue({ site }: { site: Site }) {
    const issue = useIssue(site)

    if (!issue) {
        return null
    }

    return (
        <Flex
            align="center"
            justify="space-between"
            gap="small"
            className={`border-t px-6 py-3 ${isError(site) ? 'border-red-100 bg-red-50' : 'border-orange-100 bg-orange-50'}`}
        >
            <Badge
                status={isError(site) ? 'error' : 'warning'}
                text={issue.text}
            />
            {issue.action}
        </Flex>
    )
}

/** What is wrong with a website in one sentence, and a button to where it is fixed. */
export function useIssue(site: Site): { text: string; action?: React.ReactNode } | undefined {
    const { t } = useTranslation()
    const snippet = site.type === 'snippet'
    const fix = (to: string, label: string, search?: Record<string, string>) => (
        <Link
            to={to}
            params={{ siteId: site.id }}
            search={search}
            className="shrink-0"
        >
            <Button size="small">{label}</Button>
        </Link>
    )

    switch (site.issue) {
        case 'disconnected':
            return {
                text: snippet
                    ? t('Disconnected. Add the website again to show the chat.')
                    : t('Disconnected. Connect again from the Evaa plugin in WordPress.'),
            }
        case 'no_plan':
            return {
                text: t('Choose a plan: the chat is off for visitors.'),
                action: (
                    <Link
                        to="/settings"
                        className="shrink-0"
                    >
                        <Button size="small">{t('Choose a plan')}</Button>
                    </Link>
                ),
            }
        case 'not_seen':
            return {
                text: snippet
                    ? t('The code has not loaded for more than 2 days.')
                    : t('Evaa has not heard from WordPress for more than 2 days.'),
                action: fix('/websites/$siteId/settings/connection', t('Check')),
            }
        case 'not_installed':
            return {
                text: t('The code is not on your website yet.'),
                action: fix('/websites/$siteId/settings/connection', t('Install')),
            }
        case 'chat_disabled':
            return {
                text: t('The chat is switched off.'),
                action: fix('/websites/$siteId/settings', t('Open settings')),
            }
        case 'documents_failed':
            return {
                text: t('{{count}} pages could not be read.', { count: site.failedDocumentsCount ?? 0 }),
                action: fix('/websites/$siteId/settings/knowledge', t('View pages'), { status: 'failed' }),
            }
        default:
            return undefined
    }
}
